import {
    areSearchParamsEqual,
    TRUNK_STATISTICS_ENCODED_QUERY_KEY,
    getHashSearchParams,
    unwrapEncodedSearchParams,
    wrapEncodedSearchParams,
} from './encodedUrlQuery';

export { areSearchParamsEqual };

export type TrunkStatisticsFilterItem = { key: string; value: string };

export type TrunkStatisticsUrlViewState = {
    hasAny: boolean;
    page: number;
    size: number;
    orderBy: string | null;
    isDesc: boolean;
    sortOrder: number;
    trunkId: number | string | null;
    filterId: TrunkStatisticsFilterItem | null;
    filterNumber: TrunkStatisticsFilterItem | null;
    filterDateTime: Date | null;
    filterEndDateTime: Date | null;
    startTime: Date | null;
    endTime: Date | null;
    hasAppliedSearch: boolean;
    uiFilters: Record<string, any>;
};

export type TrunkStatisticsUrlWriteState = {
    page: number;
    size: number;
    orderBy: string | null;
    isDesc: boolean;
    sortOrder: number;
    trunkId: number | string | null;
    filterId: TrunkStatisticsFilterItem | null;
    filterNumber: TrunkStatisticsFilterItem | null;
    filterDateTime: any;
    filterEndDateTime: any;
    startTime: any;
    endTime: any;
};

const DEFAULT_PAGE = 1;
const DEFAULT_SIZE = 10;

export function readInitialTrunkStatisticsUrlState(): TrunkStatisticsUrlViewState {
    return parseTrunkStatisticsUrlState(getHashSearchParams());
}

export function parseTrunkStatisticsUrlState(params: URLSearchParams): TrunkStatisticsUrlViewState {
    params = unwrapEncodedSearchParams(params, TRUNK_STATISTICS_ENCODED_QUERY_KEY);
    const uiFilters: Record<string, any> = {};
    const page = parsePositiveInt(params.get('page'), DEFAULT_PAGE);
    const size = parsePositiveInt(params.get('size'), DEFAULT_SIZE);
    const orderBy = params.get('orderBy') || null;
    const isDesc = parseBooleanParam(params.get('desc'), true);
    const sortOrder = parseSortOrder(params.get('sortOrder'), orderBy, isDesc);
    const trunkIdRaw = params.get('trunkId');
    const trunkId = trunkIdRaw && /^-?\d+$/.test(trunkIdRaw) ? Number(trunkIdRaw) : (trunkIdRaw || null);
    const id = params.get('id');
    const number = params.get('number');
    const startRange = parseDateTimeRange(params.get('startFrom'), params.get('startTo'));

    if (id) uiFilters.id = id;
    if (number) uiFilters.number = number;

    return {
        hasAny: params.toString().length > 0,
        page,
        size,
        orderBy,
        isDesc,
        sortOrder,
        trunkId,
        filterId: toFilterItem('TrunkPhoneId', id),
        filterNumber: toFilterItem('ANumber', number),
        filterDateTime: startRange?.startDate ?? null,
        filterEndDateTime: startRange?.endDate ?? null,
        startTime: startRange?.startTime ?? null,
        endTime: startRange?.endTime ?? null,
        hasAppliedSearch: !!trunkId,
        uiFilters,
    };
}

export function buildTrunkStatisticsSearchParams(state: TrunkStatisticsUrlWriteState): URLSearchParams {
    return wrapEncodedSearchParams(buildPlainTrunkStatisticsSearchParams(state), TRUNK_STATISTICS_ENCODED_QUERY_KEY);
}

function buildPlainTrunkStatisticsSearchParams(state: TrunkStatisticsUrlWriteState): URLSearchParams {
    const params = new URLSearchParams();

    if (state.page && state.page !== DEFAULT_PAGE) {
        params.set('page', String(state.page));
    }
    if (state.size && state.size !== DEFAULT_SIZE) {
        params.set('size', String(state.size));
    }
    if (state.orderBy && !(state.orderBy === 'TrunkPhoneId' && state.sortOrder === 0)) {
        params.set('orderBy', state.orderBy);
        params.set('desc', state.isDesc ? '1' : '0');
        if (state.sortOrder) {
            params.set('sortOrder', String(state.sortOrder));
        }
    }

    if (state.trunkId != null && state.trunkId !== '') {
        params.set('trunkId', String(state.trunkId));
    }

    setScalar(params, 'id', state.filterId?.value);
    setScalar(params, 'number', state.filterNumber?.value);

    if (shouldWriteStartRange(state)) {
        const startFrom = combineDateAndTime(state.filterDateTime, state.startTime, true);
        const startTo = combineDateAndTime(state.filterEndDateTime, state.endTime, false);
        if (startFrom) params.set('startFrom', toLocalIso(startFrom));
        if (startTo) params.set('startTo', toLocalIso(startTo));
    }

    return params;
}

function shouldWriteStartRange(state: TrunkStatisticsUrlWriteState): boolean {
    const startFrom = combineDateAndTime(state.filterDateTime, state.startTime, true);
    const startTo = combineDateAndTime(state.filterEndDateTime, state.endTime, false);
    if (!startFrom || !startTo) return false;
    if (state.trunkId != null && state.trunkId !== '') return true;
    return !isDefaultDateRange(startFrom, startTo);
}

function isDefaultDateRange(startFrom: Date, startTo: Date): boolean {
    const todayStart = new Date();
    todayStart.setHours(0, 0, 0, 0);
    const todayEnd = new Date();
    todayEnd.setHours(23, 59, 59, 0);
    return toLocalIso(startFrom) === toLocalIso(todayStart) && toLocalIso(startTo) === toLocalIso(todayEnd);
}

function setScalar(params: URLSearchParams, key: string, value?: string | null) {
    if (value) {
        params.set(key, value);
    }
}

function toFilterItem(key: string, value?: string | null): TrunkStatisticsFilterItem | null {
    if (!value) return null;
    return { key, value };
}

function parsePositiveInt(value: string | null, fallback: number): number {
    const parsed = Number(value);
    if (!Number.isInteger(parsed) || parsed < 1) {
        return fallback;
    }
    return parsed;
}

function parseBooleanParam(value: string | null, fallback: boolean): boolean {
    if (value == null) return fallback;
    return value === '1' || value === 'true';
}

function parseSortOrder(value: string | null, orderBy: string | null, isDesc: boolean): number {
    if (value === '1' || value === '-1' || value === '0') {
        return Number(value);
    }
    if (!orderBy) return 0;
    return isDesc ? -1 : 1;
}

function toLocalIso(date: Date): string {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    const hours = String(date.getHours()).padStart(2, '0');
    const minutes = String(date.getMinutes()).padStart(2, '0');
    const seconds = String(date.getSeconds()).padStart(2, '0');
    return `${year}-${month}-${day}T${hours}:${minutes}:${seconds}`;
}

function fromLocalIso(value: string): Date | null {
    const match = value.match(/^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2}):(\d{2})$/);
    if (!match) {
        const parsed = new Date(value);
        return Number.isNaN(parsed.getTime()) ? null : parsed;
    }
    return new Date(
        Number(match[1]),
        Number(match[2]) - 1,
        Number(match[3]),
        Number(match[4]),
        Number(match[5]),
        Number(match[6])
    );
}

function parseDateTimeRange(fromValue: string | null, toValue: string | null) {
    if (!fromValue || !toValue) return null;
    const from = fromLocalIso(fromValue);
    const to = fromLocalIso(toValue);
    if (!from || !to) return null;
    return {
        startDate: startOfDay(from),
        endDate: startOfDay(to),
        startTime: timeOnly(from),
        endTime: timeOnly(to),
    };
}

function startOfDay(date: Date): Date {
    const next = new Date(date);
    next.setHours(0, 0, 0, 0);
    return next;
}

function timeOnly(date: Date): Date {
    const next = new Date();
    next.setHours(date.getHours(), date.getMinutes(), date.getSeconds(), 0);
    return next;
}

function combineDateAndTime(dateValue: any, timeValue: any, isStart: boolean): Date | null {
    const date = toValidDate(dateValue);
    if (!date) return null;
    const time = toValidDate(timeValue);
    const result = new Date(date);
    if (time) {
        result.setHours(time.getHours(), time.getMinutes(), time.getSeconds(), 0);
    } else {
        result.setHours(isStart ? 0 : 23, isStart ? 0 : 59, isStart ? 0 : 59, 0);
    }
    return result;
}

function toValidDate(value: any): Date | null {
    if (!value) return null;
    const date = value instanceof Date ? value : new Date(value);
    return Number.isNaN(date.getTime()) ? null : date;
}
